import { createServerFn } from "@tanstack/react-start";
import { eq, desc, asc, and, inArray } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug } from "../utils/slug.server";
import { sanitizeHtml } from "../utils/sanitize.server";
import { ensureEditorDocPersisted } from "../doc/doc-migrate.server";
import { parseEditorDocInput } from "../doc/doc-types";
import { invalidateCareersCache } from "./public.functions";
import { parseCraftStateInput } from "../craft/parse-craft-input.server";

const craftStateSchema = z.union([z.string(), z.record(z.unknown())]).optional().nullable();

const careerInput = z.object({
  title: z.string().min(1),
  slug: z.string().optional(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  location: z.string().optional(),
  employmentType: z.string().optional(),
  department: z.string().optional(),
  applyEmail: z.string().optional(),
  excerpt: z.string().optional(),
  contentHtml: z.string().optional(),
  craftState: craftStateSchema,
  craftCustomCss: z.string().optional().nullable(),
  editorDoc: z.array(z.record(z.unknown())).optional().nullable(),
  sortOrder: z.number().optional(),
});

export const listCareersAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("careers", "read");
  return getDb().select().from(schema.careers).orderBy(asc(schema.careers.sortOrder));
});

export const getCareerAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("careers", "read");
    const db = getDb();
    const [row] = await db.select().from(schema.careers).where(eq(schema.careers.id, data.id)).limit(1);
    if (!row) return null;
    return ensureEditorDocPersisted(db, schema.careers, row);
  });

export const createCareer = createServerFn({ method: "POST" })
  .inputValidator(careerInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("careers", "write");
    const slug = data.slug || (await generateUniqueSlug(schema.careers, data.title));
    const editorDoc = data.editorDoc != null ? parseEditorDocInput(data.editorDoc) : null;
    const [row] = await getDb().insert(schema.careers).values({
      title: data.title,
      slug,
      status: data.status,
      location: data.location,
      employmentType: data.employmentType,
      department: data.department,
      applyEmail: data.applyEmail,
      excerpt: data.excerpt,
      contentHtml: data.contentHtml ? sanitizeHtml(data.contentHtml) : null,
      craftState: parseCraftStateInput(data.craftState),
      craftCustomCss: data.craftCustomCss ?? null,
      editorDoc,
      contentFormat: "html",
      sortOrder: data.sortOrder ?? 0,
      publishedAt: data.status === "published" ? new Date() : null,
      createdBy: user.id,
      updatedBy: user.id,
    }).returning();
    await invalidateCareersCache();
    return row;
  });

export const updateCareer = createServerFn({ method: "POST" })
  .inputValidator(careerInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("careers", "write");
    const [existing] = await getDb().select().from(schema.careers).where(eq(schema.careers.id, data.id)).limit(1);
    if (!existing) throw new Error("Not found");
    const slug = data.slug || existing.slug;
    const editorDoc =
      data.editorDoc !== undefined
        ? parseEditorDocInput(data.editorDoc)
        : (existing.editorDoc as typeof data.editorDoc) ?? null;
    const [row] = await getDb().update(schema.careers).set({
      title: data.title,
      slug,
      status: data.status,
      location: data.location,
      employmentType: data.employmentType,
      department: data.department,
      applyEmail: data.applyEmail,
      excerpt: data.excerpt,
      contentHtml: data.contentHtml ? sanitizeHtml(data.contentHtml) : existing.contentHtml,
      craftState:
        data.craftState !== undefined
          ? parseCraftStateInput(data.craftState)
          : existing.craftState,
      craftCustomCss:
        data.craftCustomCss !== undefined ? data.craftCustomCss : existing.craftCustomCss,
      editorDoc,
      sortOrder: data.sortOrder ?? existing.sortOrder,
      publishedAt: data.status === "published" ? (existing.publishedAt ?? new Date()) : existing.publishedAt,
      updatedBy: user.id,
      updatedAt: new Date(),
    }).where(eq(schema.careers.id, data.id)).returning();
    await invalidateCareersCache();
    return row;
  });

export const deleteCareer = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("careers", "delete");
    await getDb().delete(schema.careers).where(eq(schema.careers.id, data.id));
    await invalidateCareersCache();
    return { ok: true };
  });

export const reorderCareers = createServerFn({ method: "POST" })
  .inputValidator(z.object({ ids: z.array(z.string().uuid()).min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("careers", "write");
    const db = getDb();
    for (let i = 0; i < data.ids.length; i++) {
      await db
        .update(schema.careers)
        .set({ sortOrder: i, updatedAt: new Date() })
        .where(and(eq(schema.careers.id, data.ids[i]), inArray(schema.careers.id, data.ids)));
    }
    await invalidateCareersCache();
    return { ok: true };
  });
